<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { onMounted, computed } from "vue";
import { useCuratedStore } from "@/stores/curatedStore";
import {useUserStore} from "@/stores/userStore";

const { t, n } = useI18n();
const curatedStore = useCuratedStore();
const userStore = useUserStore();

// Fetch curated offers on component mount
onMounted(() => {
  curatedStore.fetchCuratedOffers();
});

// Access reactive store properties
const curatedOffers = computed(() => curatedStore.curatedOffers);

// Helper function to chunk offers into rows
const chunkRows = (offers) => {
  const rows = [];
  let isOddRow = true;

  for (let i = 0; i < offers.length; ) {
    if (isOddRow) {
      rows.push({ items: offers.slice(i, i + 3), isOddRow: true });
      i += 3;
    } else {
      rows.push({ items: offers.slice(i, i + 4), isOddRow: false });
      i += 4;
    }
    isOddRow = !isOddRow;
  }
  return rows;
};
</script>

<template>
  <div class="container mx-auto my-10">
    <!-- Section Title -->
    <span class="responsive-title-2 block">{{ t("curated.title") }}</span>

    <div v-if="curatedOffers.length === 0" class="text-center py-8">
      <div class="flex justify-center items-center min-h-[300px]">
        <div class="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    </div>

    <div v-else class="pt-8 flex flex-col gap-4">
      <span class="font-semibold responsive-title-6">{{ t("curated.subtitle") }}</span>
      <!-- Desktop View -->
      <div class="hidden md:flex flex-col gap-6">
        <template v-for="(row, rowIndex) in chunkRows(curatedOffers)" :key="`row-${rowIndex}`">
          <div
              :class="[
              row.isOddRow ? 'grid gap-6' : 'grid gap-6',
              'grid-cols-1 sm:grid-cols-1 lg:grid-cols-4',
            ]"
          >
            <template v-for="(offer, colIndex) in row.items" :key="offer.id">
              <div
                  :class="[
                  'relative overflow-hidden rounded-lg h-[300px] sm:h-[377px] group',
                  row.isOddRow && colIndex === 1 ? 'sm:col-span-2' : '',
                  row.isOddRow && (colIndex === 0 || colIndex === 2) ? 'col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-1' : '',
                ]"
              >
                <img
                    :src="offer.imageSet['max']"
                    :alt="offer.title"
                    class="w-full h-full object-cover brightness-70"
                />

                <div class="absolute inset-0 flex flex-col justify-between p-6 text-white">
                  <h3 class="responsive-title-5 font-semibold">{{ offer.title }}</h3>

                  <div class="flex flex-col gap-2 mt-4">
                    <div
                        v-for="destination in offer.destinations"
                        :key="destination.name"
                        class="flex flex-row items-center gap-4"
                    >
                      <a
                          :href="destination.url"
                          rel="noopener noreferrer"
                          class="custom-hover-effect destination-border border rounded-full px-4 py-2 text-white bg-transparent transition duration-300"
                      >
                        <span class="responsive-body-small font-semibold">{{ destination.name }}</span>
                      </a>
                      <span class="responsive-body-small font-semibold">
                        {{ t("curated.fromPrice") }} {{ userStore.formatCurrency(destination.price) }}/week
                      </span>
                    </div>
                  </div>

                </div>
              </div>
            </template>
          </div>
        </template>
      </div>

      <!-- Mobile View -->
      <div class="md:hidden flex flex-col gap-8">
        <div class="flex gap-5 overflow-x-auto">
          <div
              v-for="offer in curatedOffers"
              :key="offer.id"
              class="flex flex-col gap-2 min-w-[320px]"
          >
            <div class=" min-h-[300px] relative aspect-w-4 h-60 mb-2 overflow-hidden rounded-xl">
              <img
                  :src="offer.imageSet['max']"
                  :alt="offer.title"
                  class="w-full h-full object-cover brightness-70"
              />
              <div class="absolute inset-0 flex flex-col justify-between p-4 text-white bg-gradient-to-t from-black/60 to-transparent">
                <h3 class="text-base responsive-title-5 font-semibold">{{ offer.title }}</h3>
                <div class="flex flex-col gap-2">
                  <div
                      v-for="destination in offer.destinations"
                      :key="destination.name"
                      class="flex items-center gap-2"
                  >
                    <a
                        :href="destination.url"
                        rel="noopener noreferrer"
                        class="destination-chip responsive-body-xsmall font-semibold"
                    >
                      {{ destination.name }}
                    </a>
                    <span class="responsive-body-small font-semibold">
                      {{ t("curated.fromPrice") }} {{ userStore.formatCurrency(destination.price) }}/week
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.destination-border {
  border-color: white !important;
}

.custom-hover-effect {
  background-color: transparent;
  color: white;
  border: 1px solid white;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.custom-hover-effect:hover {
  background-color: white !important;
  color: black !important;
}

/* Mobile Destination Styling */
.destination-chip {
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid white;
  text-transform: capitalize;
  transition: background 0.3s ease;
}

.destination-chip:hover {
  background: white;
  color: black;
}
</style>